/**
 * ⚠️ TODO — REM7 ASSETS PENDING
 * ------------------------------------------------------------------
 * This page currently BORROWS the LUNA M9 product photography
 * (`src/assets/luna-m9-*`) and the LUNA demo video
 * (`/videos/luna-m9-demo.mp4`) as placeholders.
 *
 * REM7 is a distinct flagship product from LUNA (and from LUXA).
 * Real REM7 studio photography (hero, profile, control panel,
 * installed lifestyle) and a real REM7 demo video must be
 * commissioned and swapped in here, in the Product JSON-LD
 * `image` array, and in the SEO `image` prop, before this page
 * is fully correct.
 *
 * Until then: keep REM7 copy, specs and positioning written
 * independently — do NOT reuse LUNA/LUXA marketing language.
 * ------------------------------------------------------------------
 */
import { useState, useEffect } from "react";
import { ArrowUp, ArrowRight, Download, Shield, Droplets, Thermometer, Zap, Heart, Sparkles, Wrench, FileText, HelpCircle, CheckCircle } from "lucide-react";
// Link removed - using LocalizedLink for all internal navigation
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { FeatureCardSimple } from "@/components/ui/feature-card-simple";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Navigation from "@/components/Navigation";
import { SeoHead } from "@/components/SeoHead";
import { getSeoCopy } from "@/i18n/seoCopy";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { useOptimizedObserver } from "@/hooks/useOptimizedObserver";
import ImageLightbox from "@/components/ui/image-lightbox";
import { ProductGallery } from "@/components/ui/product-gallery";
import PDFDownloadButton from "@/components/ui/pdf-download-button";
import OptimizedVideo from "@/components/ui/optimized-video";
import FAQ from "@/components/FAQ";
import { productFAQs } from "@/data/faqData";
import { RelatedProducts } from "@/components/RelatedProducts";
import { Footer } from "@/components/Footer";
import { LocalizedLink } from "@/components/LocalizedLink";
import { useI18n } from "@/i18n";
import lunaMainImage from "@/assets/luna-m9-main-optimized.webp";
import lunaMainImageFallback from "@/assets/luna-m9-main.jpg";
import lunaAngleImage from "@/assets/luna-m9-angle.jpg";
import lunaDisplayImage from "@/assets/luna-m9-display.webp";
import lunaInstalledImage from "@/assets/luna-m9-installed.jpg";
import lunaControlsImage from "@/assets/luna-m9-controls.png";

import { PriceFrom } from "@/components/products/PriceFrom";
export default function REM7() {
  const [showBackToTop, setShowBackToTop] = useState(false);
  const { t, language } = useI18n();
  const seo = getSeoCopy("rem7", language);
  useScrollToTop();
  useOptimizedObserver();

  useEffect(() => {
    let rafId: number | null = null;
    
    const handleScroll = () => {
      if (rafId) return;
      
      rafId = requestAnimationFrame(() => {
        setShowBackToTop(window.scrollY > 400);
        rafId = null;
      });
    };
    
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const productImages = [
    { src: lunaMainImage, alt: "REM7 flagship smart toilet with instant heating and wellness spa features", title: "Main Design" },
    { src: lunaAngleImage, alt: "REM7 flagship smart toilet profile view showing elegant design and digital controls", title: "Profile View" },
    { src: lunaDisplayImage, alt: "REM7 flagship smart toilet digital display with temperature and pressure controls", title: "Digital Controls" },
    { src: lunaInstalledImage, alt: "REM7 flagship smart toilet installed in premium modern bathroom setting", title: "Installation" },
    { src: lunaControlsImage, alt: "REM7 flagship smart toilet wireless remote control panel with advanced bidet functions", title: "Control Panel" }
  ];

  const keyFeatures = [
    "Tankless instant-heat water delivery — no reservoir, no waiting between users",
    "Flagship digital interface with precise water and seat temperature control",
    "Dual-nozzle bidet with independent rear and feminine wash programmes",
    "Oscillating and pulsating wash modes with adjustable pressure",
    "Warm-air drying with selectable temperature stages",
    "Proximity-sensor automatic lid and seat operation",
    "Ambient night illumination for low-light use",
    "UV-assisted self-sanitising nozzle cycle before and after every use",
    "Multi-user memory profiles for hotel and multi-occupancy projects",
    "Eco standby mode for high-volume specification projects",
    "Antimicrobial seat surface engineered for commercial duty cycles"
  ];

  const technicalSpecs = [
    { label: "Model", value: "REM7 (flagship series)" },
    { label: "Series Variants", value: "M3 · S1 · K2 · U1" },
    { label: "Dimensions", value: "715 × 625 × 465 mm" },
    { label: "Heating Type", value: "Instant heating system" },
    { label: "S-trap Rough-in", value: "300±400 mm (customizable 200-450 mm)" },
    { label: "P-trap Rough-in", value: "~100 mm" },
    { label: "Water Pressure", value: "0.07-0.75 MPa" },
    { label: "Power Consumption", value: "1200W max" },
    { label: "Water Protection", value: "IPX4 Waterproof" },
    { label: "Seat Material", value: "Antimicrobial composite" },
    { label: "Weight", value: "35kg" }
  ];

  const installationRequirements = [
    {
      title: "Electrical Requirements",
      items: [
        "220-240V AC power supply",
        "Dedicated GFCI outlet required",
        "Professional electrical installation recommended"
      ]
    },
    {
      title: "Plumbing Requirements",
      items: [
        "Water supply line (cold water)",
        "Drain connection compatible with S-trap or P-trap",
        "Water pressure: 0.07-0.75 MPa",
        "Shut-off valve accessible for maintenance"
      ]
    },
    {
      title: "Space Requirements",
      items: [
        "Minimum 715mm width clearance",
        "625mm depth from wall center",
        "465mm height clearance",
        "Additional space for seat opening/closing"
      ]
    }
  ];

  const faqItems = [
    {
      question: "What are the exact dimensions and will it fit my bathroom?",
      answer: "The REM7 measures 715mm × 625mm × 465mm. It requires a minimum clearance width of 715mm and depth of 625mm from the wall center. Please measure your space carefully and consult our installation guide."
    },
    {
      question: "What type of rough-in do I need?",
      answer: "The REM7 supports both S-trap (300±400mm, customizable 200-450mm) and P-trap (~100mm) configurations. The rough-in can be customized during installation to fit your existing plumbing."
    },
    {
      question: "Do I need special electrical work?",
      answer: "Yes, you'll need a dedicated 220-240V GFCI outlet near the toilet. Professional electrical installation is recommended for safety and warranty compliance."
    },
    {
      question: "How does the instant heating work?",
      answer: "The REM7 features an advanced instant heating system that provides immediate warm water without a storage tank, ensuring consistent temperature and energy efficiency."
    },
    {
      question: "Can I install it myself?",
      answer: "While the REM7 comes with detailed installation instructions, we recommend professional installation to ensure proper plumbing, electrical connections, and warranty coverage."
    },
    {
      question: "What maintenance is required?",
      answer: "Regular cleaning with mild soap, monthly nozzle cleaning cycles (automated), and annual professional maintenance check-ups are recommended for optimal performance."
    }
  ];

  // Clean Product schema: identity only — no offers, price, sku, ratings or specs.
  const productStructuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "REM7 Smart Toilet",
    "description": "REM7 is Aureum VII Trading's flagship smart toilet, combining tankless instant heating, adjustable washing, warm-air drying and touch-free convenience. Stock is in production for Spain and arriving soon; private demonstrations and project enquiries can be requested now.",
    "category": "Smart Toilets",
    "url": "https://www.aureum7trading.com/products/rem7",
    "image": "https://www.aureum7trading.com/images/products/luna-m9-main.jpg",
    "brand": {
      "@type": "Brand",
      "name": "Aureum VII Trading"
    },
    "seller": {
      "@type": "Organization",
      "name": "Aureum VII Trading",
      "url": "https://www.aureum7trading.com"
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary via-background to-muted text-foreground">
      <SeoHead
        route="rem7"
        schema={productStructuredData}
        image="https://www.aureum7trading.com/images/products/luna-m9-main.jpg"
      />
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-28 pb-14 px-4 sm:px-6 lg:px-12">
        <div className="container mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="fade-in-observer">
              <Badge variant="outline" className="border-accent/30 text-accent bg-accent/5 mb-4 font-medium">
                Flagship Series · Launching in Spain
              </Badge>
              <h1 className="font-playfair text-5xl md:text-6xl font-light mb-6 text-primary">
                {seo.h1}
              </h1>
              <p className="text-2xl md:text-3xl text-muted-foreground font-medium mb-6 font-playfair">
                Premium Smart Toilet for Spain &amp; Europe
              </p>
              <p className="font-inter text-xl text-muted-foreground mb-8 leading-relaxed">
                REM7 is Aureum VII Trading's flagship smart toilet, combining tankless instant heating, adjustable washing, warm-air drying and touch-free convenience. Stock is in production for Spain and arriving soon; private demonstrations and project enquiries can be requested now.
              </p>

              <PriceFrom price={600} />

              {/* Features List */}
              <div className="mb-8 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-accent rounded-full"></div>
                  <span className="font-inter text-muted-foreground">Instant heating with digital display</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-accent rounded-full"></div>
                  <span className="font-inter text-muted-foreground">Multiple wash modes with adjustable pressure</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-accent rounded-full"></div>
                  <span className="font-inter text-muted-foreground">Integrated warm air drying system</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-accent rounded-full"></div>
                  <span className="font-inter text-muted-foreground">Auto open/close with night light</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button asChild className="min-h-[48px]"><LocalizedLink to="/contact?intent=trade&message=REM7%20pre-order%20enquiry#enquiry-form">Pre-order enquiry <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" /></LocalizedLink></Button>
                <Button variant="outline" asChild className="min-h-[48px]"><LocalizedLink to="/contact?intent=trade">Request Trade / Project Pricing</LocalizedLink></Button>
              </div>
              <p className="mt-4 text-sm text-muted-foreground">Enquiry only — no payment or reservation. Availability, specifications and project terms are confirmed in writing.</p>
            </div>
            <div className="fade-in-observer">
              <div className="relative">
                <picture>
                  <source srcSet={lunaMainImage} type="image/webp" />
                  <img 
                    src={lunaMainImageFallback} 
                    alt="REM7 flagship smart toilet featuring therapeutic massage functions, instant water heating technology, advanced air purification system, and premium comfort-focused design"
                    className="w-full h-auto rounded-2xl shadow-luxury"
                    width="600"
                    height="800"
                    loading="eager"
                  />
                </picture>
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent rounded-2xl" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Video */}
      <section className="py-16 px-6 lg:px-12 bg-white/60 backdrop-blur-sm">
        <div className="container mx-auto max-w-5xl">
          <div className="fade-in-observer text-center mb-12">
            <h2 className="font-playfair text-3xl font-medium text-primary mb-4">See REM7 in Action</h2>
            <p className="font-inter text-muted-foreground max-w-2xl mx-auto">
              A closer look at the intelligent bathroom experience available across the Aureum VII range. Final REM7-specific media is being prepared.
            </p>
          </div>
          <OptimizedVideo
            // TODO: replace with real REM7 demo video once filmed
            src="/videos/luna-m9-demo.mp4"
            poster={lunaDisplayImage}
            controls
            className="aspect-video rounded-2xl overflow-hidden shadow-luxury bg-slate-100"
            aria-label="REM7 smart toilet demonstration video"
          />
        </div>
      </section>

      {/* Product Gallery */}
      <section className="py-16 px-6 lg:px-12">
        <div className="container mx-auto max-w-7xl">
          <div className="fade-in-observer">
            <h2 className="font-playfair text-3xl font-medium text-primary mb-12 text-center">Product Gallery</h2>
            <ProductGallery images={productImages} className="max-w-4xl mx-auto" />
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section id="specifications" className="scroll-mt-28 py-16 px-4 sm:px-6 lg:px-12 bg-muted/30">
        <div className="container mx-auto max-w-7xl">
          <div className="fade-in-observer">
            <h2 className="font-playfair text-3xl font-medium text-primary mb-12 text-center">Technical Specifications</h2>
            
            <Tabs defaultValue="specs" className="w-full">
              <TabsList className="grid h-auto w-full grid-cols-2 gap-1 mb-8 sm:grid-cols-4">
                <TabsTrigger value="specs">Specifications</TabsTrigger>
                <TabsTrigger value="features">Key Features</TabsTrigger>
                <TabsTrigger value="installation">Installation</TabsTrigger>
                <TabsTrigger value="warranty">Warranty</TabsTrigger>
              </TabsList>
              
              <TabsContent value="specs" className="fade-in-observer">
                <Card>
                  <CardContent className="p-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      {technicalSpecs.map((spec, index) => (
                        <div key={index} className="flex justify-between py-3 border-b border-border/50 last:border-0">
                          <span className="font-inter font-medium text-muted-foreground">{spec.label}</span>
                          <span className="font-inter text-foreground">{spec.value}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-6 pt-6 border-t border-border/50 flex flex-wrap items-center justify-between gap-4">
                      <div>
                        <p className="font-playfair text-base font-semibold text-primary">REM7 Specifications Document</p>
                        <p className="font-inter text-xs text-muted-foreground">Full technical sheet, installation requirements, warranty &amp; care guide.</p>
                      </div>
                      <a
                        href="/files/REM7_Specifications.pdf"
                        download
                        className="inline-flex items-center gap-2 rounded-md border border-accent/40 bg-accent/10 hover:bg-accent/20 text-accent px-4 py-2 font-inter text-sm font-semibold tracking-wide transition-colors"
                      >
                        <Download className="h-4 w-4" />
                        Download REM7 PDF
                      </a>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="features" className="fade-in-observer">
                <Card>
                  <CardContent className="p-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      {keyFeatures.map((feature, index) => (
                        <div key={index} className="flex items-center gap-3 py-2">
                          <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                          <span className="font-inter text-foreground">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent id="installation" value="installation" className="fade-in-observer scroll-mt-28">
                <div className="grid md:grid-cols-3 gap-6">
                  {installationRequirements.map((req, index) => (
                    <Card key={index}>
                      <CardContent className="p-6">
                        <h3 className="font-playfair text-lg font-medium text-primary mb-4">{req.title}</h3>
                        <ul className="space-y-2">
                          {req.items.map((item, idx) => (
                            <li key={idx} className="flex items-start gap-2 font-inter text-sm text-muted-foreground">
                              <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
              
              <TabsContent value="warranty" className="fade-in-observer">
                <Card>
                  <CardContent className="p-6">
                    <h3 className="font-playfair text-xl text-primary mb-3">Warranty and after-sales information</h3>
                    <p className="font-inter text-muted-foreground leading-relaxed">Applicable warranty terms, covered components and service arrangements are confirmed in writing with your quotation. Ask our team for the current REM7 warranty document before ordering.</p>
                    <Button asChild variant="outline" className="mt-5"><LocalizedLink to="/contact?intent=quote">Request warranty information</LocalizedLink></Button>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </section>

      <section className="py-14 px-4 sm:px-6 lg:px-12 bg-primary text-primary-foreground">
        <div className="container mx-auto max-w-6xl text-center">
          <p className="text-sm font-semibold uppercase tracking-widest text-accent mb-3">Spain pre-launch</p>
          <h2 className="font-playfair text-3xl sm:text-4xl mb-4">Choose the right enquiry for you</h2>
          <p className="mx-auto max-w-3xl text-primary-foreground/75 mb-8">Stock is in production and arriving in Spain soon. Homeowners can request a private demonstration; professionals can discuss specifications, quantities and project pricing.</p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <Button asChild className="bg-accent text-accent-foreground"><LocalizedLink to="/contact?intent=demo">Request a Private Demo</LocalizedLink></Button>
            <Button asChild variant="outline" className="border-primary-foreground/40 bg-transparent text-primary-foreground hover:bg-primary-foreground/10"><LocalizedLink to="/contact?intent=trade">Request Trade / Project Pricing</LocalizedLink></Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-6 lg:px-12 bg-white/60">
        <div className="container mx-auto max-w-4xl">
          <div className="fade-in-observer text-center mb-12">
            <h2 className="font-playfair text-3xl font-medium text-primary mb-4">
              Frequently Asked Questions
            </h2>
            <p className="font-inter text-muted-foreground">
              Find answers to common questions about the REM7 smart toilet.
            </p>
          </div>
          <FAQ items={faqItems} />
        </div>
      </section>

      {/* Related Products */}
      <section className="py-16 px-6 lg:px-12">
        <div className="container mx-auto max-w-7xl">
          <div className="fade-in-observer">
            <h2 className="font-playfair text-3xl font-medium text-primary mb-12 text-center">
              Other Aureum VII Collections
            </h2>
            <RelatedProducts currentProduct="REM7" />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 lg:px-12 bg-primary text-primary-foreground">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="font-playfair text-3xl md:text-4xl font-light mb-6">
            Start your REM7 enquiry
          </h2>
          <p className="font-inter text-lg mb-8 opacity-90">
            Tell our team whether you are planning for a home, a private demonstration or a professional project.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="secondary">
              <LocalizedLink to="/contact?intent=trade&message=REM7%20pre-order%20enquiry#enquiry-form">Pre-order enquiry</LocalizedLink>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white/30 hover:bg-white/10">
              <LocalizedLink to="/contact?intent=demo">Book a Private Demo</LocalizedLink>
            </Button>
          </div>
        </div>
      </section>

      {/* Back to Top */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 bg-accent text-accent-foreground p-3 rounded-full shadow-lg hover:bg-accent/90 transition-all z-50"
          aria-label="Back to top"
        >
          <ArrowUp className="w-5 h-5" />
        </button>
      )}

      <Footer />
    </div>
  );
}
